import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import {
  Breadcrumb,
  BreadcrumbItem,
  Content,
  Icon,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
} from '@patternfly/react-core';
import { KeyIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import Unauthenticated from '@app/Unauthenticated';
import PubkeyForm from './PubkeyForm';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useUserContext } from '../../../contexts/user/UserContext';
import { Pubkey, PubkeyRequest } from '../../../types/models/Pubkey';
import { ApiResponsePubkey } from '../../../types/api/ApiResponsePubkey';

const PubkeyEdit: React.FunctionComponent = () => {
  const { pubkeyId } = useParams<{ pubkeyId: string }>();
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const navigate = useNavigate();
  const currentUser = useUserContext();
  const { addNotification, setDrawerOpen } = useNotifications();

  const [pubkey, setPubkey] = useState<Pubkey | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const fetchPubkey = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch(`/datacenters/pubkeys/${pubkeyId}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = (await response.json()) as ApiResponsePubkey;
      if (data.status === 'success' && data.data?.pubkey) {
        const fetched = data.data.pubkey;
        if (currentUser === null || fetched.userID !== currentUser.id) {
          navigate(`/datacenter/pubkeys/${pubkeyId}`, { replace: true });
          return;
        }
        setPubkey(fetched);
      } else {
        throw new Error('Public key not found');
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load public key'));
    } finally {
      setLoading(false);
    }
  }, [pubkeyId, apiFetch, currentUser, navigate]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      fetchPubkey();
    }
  }, [auth.isAuthenticated, fetchPubkey]);

  const handleSubmit = async (values: PubkeyRequest) => {
    setIsSubmitting(true);
    setValidationErrors({});

    try {
      const response = await apiFetch(`/datacenters/pubkeys/${pubkeyId}`, {
        method: 'PUT',
        headers: { Accept: 'application/json' },
        body: JSON.stringify(values),
      });

      const result = (await response.json()) as ApiResponsePubkey;

      if (result.status === 'success' && result.data?.pubkey) {
        addNotification({
          title: 'Public key updated',
          srTitle: 'Success alert',
          description: `Public key "${values.label}" has been updated successfully.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        navigate(`/datacenter/pubkeys/${pubkeyId}`);
      } else if (result.status === 'fail') {
        setValidationErrors(result.data as unknown as Record<string, string>);
      } else {
        throw new Error('Failed to update public key');
      }
    } catch (err) {
      addNotification({
        title: 'Error updating public key',
        srTitle: 'Error alert',
        description: err instanceof Error ? err.message : 'An unexpected error occurred.',
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  const renderContent = () => {
    if (loading) {
      return <CenteredSpinner />;
    }

    if (error || !pubkey) {
      return (
        <Content component="p" className="pf-v6-u-text-align-center pf-v6-u-mt-xl">
          {error ? `Error loading public key: ${error.message}` : 'Public key not found.'}
        </Content>
      );
    }

    return (
      <PubkeyForm
        initialValues={{ label: pubkey.label, content: pubkey.content }}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        submitLabel="Save changes"
        validationErrors={validationErrors}
      />
    );
  };

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem to="/datacenter">Datacenter</BreadcrumbItem>
          <BreadcrumbItem to="/datacenter/pubkeys">Public Keys</BreadcrumbItem>
          <BreadcrumbItem to={`/datacenter/pubkeys/${pubkeyId}`}>{pubkey?.label ?? '...'}</BreadcrumbItem>
          <BreadcrumbItem isActive>Edit</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Content component="h1">
          <Icon size="lg" style={{ marginRight: '8px' }}>
            <KeyIcon />
          </Icon>
          Edit public key
        </Content>
        <Content component="p">Modify the label or content of your SSH public key</Content>
      </PageSection>
      <PageSection hasBodyWrapper={false}>{renderContent()}</PageSection>
    </>
  );
};

export default PubkeyEdit;
